<template>
  <div class="tw:flex tw:flex-col tw:gap-4" data-testid="reg-company-address">
    <span class="tw:text-13 tw:font-semibold tw:text-gray-900">{{ $t('registration.company.section_address') }}</span>

    <!-- UID first: confirming it can autofill the name and address below -->
    <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
      <LeadInput
        v-model="uid"
        type="text"
        required
        :label="$t('registration.company.uid')"
        :errors="combinedUidErrors"
        data-testid="reg-company-uid"
        @keydown="onUidKeydown"
        @blur="onUidBlur">
        <template v-if="uidIconEnd" #iconEnd>
          <component :is="uidIconEnd" :size="16" :color="uidIconColor" />
        </template>
        <template #hint>
          <span :class="uidHintClass">{{ uidHint }}</span>
        </template>
      </LeadInput>

      <LeadInput
        v-model="invoiceEmail"
        type="email"
        autocomplete="email"
        :label="$t('registration.company.invoice_email')"
        :placeholder="workEmail"
        :errors="errors.invoiceEmail"
        data-testid="reg-company-invoice-email">
        <template #iconStart><IconMail :size="16" /></template>
      </LeadInput>
    </div>

    <div class="tw:grid tw:grid-cols-2 tw:gap-4 tw:sm:grid-cols-[2fr_1fr_1.4fr]">
      <LeadInput
        v-model="street"
        type="text"
        autocomplete="street-address"
        class="tw:col-span-2 tw:sm:col-span-1"
        :label="$t('registration.company.street')"
        :errors="errors.street"
        required
        data-testid="reg-company-street" />

      <LeadInput
        v-model="postcode"
        type="text"
        autocomplete="postal-code"
        :label="$t('registration.company.postcode')"
        :errors="errors.postcode"
        required
        data-testid="reg-company-postcode" />

      <LeadInput
        v-model="city"
        type="text"
        autocomplete="address-level2"
        :label="$t('registration.company.city')"
        :errors="errors.city"
        required
        data-testid="reg-company-city" />
    </div>

    <LeadCheckbox v-model="billingSameAsCompany" data-testid="reg-company-same-address">
      {{ $t('registration.company.same_address') }}
    </LeadCheckbox>

    <div v-if="!billingSameAsCompany" class="tw:grid tw:grid-cols-2 tw:gap-4 tw:sm:grid-cols-[2fr_1fr_1.4fr]">
      <LeadInput
        v-model="billingStreet"
        type="text"
        autocomplete="street-address"
        class="tw:col-span-2 tw:sm:col-span-1"
        :label="$t('registration.company.billing_street')"
        :errors="errors.billingStreet"
        required
        data-testid="reg-company-billing-street" />

      <LeadInput
        v-model="billingPostcode"
        type="text"
        autocomplete="postal-code"
        :label="$t('registration.company.billing_postcode')"
        :errors="errors.billingPostcode"
        required
        data-testid="reg-company-billing-postcode" />

      <LeadInput
        v-model="billingCity"
        type="text"
        autocomplete="address-level2"
        :label="$t('registration.company.billing_city')"
        :errors="errors.billingCity"
        required
        data-testid="reg-company-billing-city" />
    </div>

    <LiConfirmationDialog
      v-model:dialogShow="showAddressReplaceModal"
      :confirmButtonLabel="$t('registration.company.address_replace_confirm')"
      :cancelButtonLabel="$t('btn.cancel')"
      :confirmActionDelay="0"
      @onContinue="confirmAddressReplace"
      @onCancel="cancelAddressReplace">
      <h1 class="mb-3">{{ $t('registration.company.address_replace_title') }}</h1>
      <p>{{ $t('registration.company.address_replace_description', { address: pendingAddressLabel }) }}</p>
    </LiConfirmationDialog>
  </div>
</template>

<script setup lang="ts">
/**
 * Validates the UID against the Swiss register on Enter/blur; a refusal blocks the step, but a
 * register outage does not (`uidIsValid` stays true). A confirmed match can autofill the name and
 * address, behind a confirm prompt if the registrant already typed an address.
 */
import { computed, ref, watch, watchEffect } from 'vue';

import { registrationClient } from '@/api/registration';
import LeadCheckbox from '@/components/lead-design/LeadCheckbox.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';
import { readRegistrationErrorMessage } from '@/components/registration/subcomponents/registrationErrors';
import translator from '@/locale/translator';
import type { LookupUidResponse, RegistrationDraft } from '@/types/registration';
import { isValidUid, normalizeUid } from '@/utils/uid';
import { ConfirmationDialog as LiConfirmationDialog } from '@prospective/lithium';
import { IconMail, IconRosetteDiscountCheck, IconX } from '@tabler/icons-vue';

type UidCheckStatus = 'idle' | 'checking' | 'valid' | 'outage' | 'invalid' | 'error';

/* ----------------------------------- STATE ---------------------------------- */
const props = defineProps<{
  errors: {
    street: string;
    postcode: string;
    city: string;
    invoiceEmail: string;
    billingStreet: string;
    billingPostcode: string;
    billingCity: string;
    uid: string;
  };
  workEmail: string;
}>();

const draft = defineModel<RegistrationDraft['company']>('draft', { required: true });

/** Whether the step may advance: true once confirmed, and also true (not blocking) on an outage. */
const uidIsValid = defineModel<boolean>('uidIsValid', { required: true });

/** Whether the displayed name is exactly the one the register confirmed. */
const nameConfirmed = defineModel<boolean>('nameConfirmed', { required: true });

const uidCheckStatus = ref<UidCheckStatus>('idle');
const uidCheckCompanyName = ref('');
/** Backend-translated outage notice or refusal. */
const uidCheckMessage = ref('');
/** The normalized UID and name the last resolved status belongs to. */
const lastCheckedUid = ref('');
const lastCheckedName = ref('');

const showAddressReplaceModal = ref(false);
const pendingAddress = ref<{ street: string; postcode: string; city: string } | null>(null);

/* --------------------------------- COMPUTED --------------------------------- */
const street = computed({
  get: () => draft.value.street,
  set: (value: string) => {
    draft.value.street = value;
  },
});

const postcode = computed({
  get: () => draft.value.postcode,
  set: (value: string) => {
    draft.value.postcode = value;
  },
});

const city = computed({
  get: () => draft.value.city,
  set: (value: string) => {
    draft.value.city = value;
  },
});

const invoiceEmail = computed({
  get: () => draft.value.invoiceEmail,
  set: (value: string) => {
    draft.value.invoiceEmail = value;
  },
});

const uid = computed({
  get: () => draft.value.uid,
  set: (value: string) => {
    draft.value.uid = value;
  },
});

const billingSameAsCompany = computed({
  get: () => draft.value.billingSameAsCompany,
  set: (value: boolean) => {
    draft.value.billingSameAsCompany = value;
  },
});

const billingStreet = computed({
  get: () => draft.value.billingStreet,
  set: (value: string) => {
    draft.value.billingStreet = value;
  },
});

const billingPostcode = computed({
  get: () => draft.value.billingPostcode,
  set: (value: string) => {
    draft.value.billingPostcode = value;
  },
});

const billingCity = computed({
  get: () => draft.value.billingCity,
  set: (value: string) => {
    draft.value.billingCity = value;
  },
});

const uidIconEnd = computed(() => {
  if (uidCheckStatus.value === 'valid') return IconRosetteDiscountCheck;
  if (uidCheckStatus.value === 'invalid' || uidCheckStatus.value === 'error') return IconX;
  return undefined;
});

// the tw prefix also applies to theme custom properties: var(--tw-color-*), not var(--color-*)
const uidIconColor = computed(() => {
  if (uidCheckStatus.value === 'valid') return 'var(--tw-color-success-700)';
  if (uidCheckStatus.value === 'invalid' || uidCheckStatus.value === 'error') return 'var(--tw-color-danger)';
  return undefined;
});

/** Informational, non-blocking text. */
const uidHint = computed(() => {
  switch (uidCheckStatus.value) {
    case 'checking':
      return translator.get('registration.company.uid_checking');
    case 'valid':
      return translator.get('registration.company.uid_confirmed', { name: uidCheckCompanyName.value });
    case 'outage':
      return uidCheckMessage.value;
    default:
      return translator.get('registration.company.uid_hint');
  }
});

const uidHintClass = computed(() => (uidCheckStatus.value === 'valid' ? 'tw:text-success-700' : undefined));

/** The blocking message; the live check wins, the submit error is the fallback (UID claimed in between). */
const combinedUidErrors = computed(() => {
  if (uidCheckStatus.value === 'invalid') return translator.get('registration.company.uid_invalid');
  if (uidCheckStatus.value === 'error') return uidCheckMessage.value;
  return props.errors.uid;
});

const pendingAddressLabel = computed(() => {
  if (!pendingAddress.value) return '';
  const { street: addrStreet, postcode: addrPostcode, city: addrCity } = pendingAddress.value;
  return `${addrStreet}, ${addrPostcode} ${addrCity}`;
});

/* --------------------------------- METHODS ---------------------------------- */
/** Idle until the UID is complete enough to judge, including mid-suffix. */
function classifyLocalUid(normalized: string): 'idle' | 'invalid' {
  if (normalized === '' || normalized.length < 12 || normalized.length === 13 || normalized.length === 14) {
    return 'idle';
  }
  return isValidUid(normalized) ? 'idle' : 'invalid';
}

/** Whether the fields changed since the lookup was fired. */
function isStaleAnswer(normalizedUid: string, requestedName: string): boolean {
  return normalizeUid(uid.value) !== normalizedUid || draft.value.name !== requestedName;
}

/** Whether the last resolved check already covered this UID and name. */
function alreadyCheckedFor(normalizedUid: string): boolean {
  return normalizedUid === lastCheckedUid.value && draft.value.name === lastCheckedName.value;
}

/** Case- and whitespace-insensitive. */
function sameAddressField(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function applyAutofill(response: LookupUidResponse) {
  if (draft.value.name.trim() === '' && response.companyName) {
    draft.value.name = response.companyName;
  }

  if (!response.street && !response.postcode && !response.city) return;

  const registryAddress = {
    street: response.street ?? '',
    postcode: response.postcode ?? '',
    city: response.city ?? '',
  };

  const addressIsEmpty =
    draft.value.street.trim() === '' && draft.value.postcode.trim() === '' && draft.value.city.trim() === '';

  if (addressIsEmpty) {
    draft.value.street = registryAddress.street;
    draft.value.postcode = registryAddress.postcode;
    draft.value.city = registryAddress.city;
    return;
  }

  // already matches the register
  if (
    sameAddressField(draft.value.street, registryAddress.street) &&
    sameAddressField(draft.value.postcode, registryAddress.postcode) &&
    sameAddressField(draft.value.city, registryAddress.city)
  ) {
    return;
  }

  pendingAddress.value = registryAddress;
  showAddressReplaceModal.value = true;
}

function confirmAddressReplace() {
  if (pendingAddress.value) {
    draft.value.street = pendingAddress.value.street;
    draft.value.postcode = pendingAddress.value.postcode;
    draft.value.city = pendingAddress.value.city;
  }
  pendingAddress.value = null;
}

function cancelAddressReplace() {
  pendingAddress.value = null;
}

async function runCheck(normalizedUid: string) {
  const requestedName = draft.value.name;

  uidCheckStatus.value = 'checking';
  uidIsValid.value = false;
  lastCheckedUid.value = normalizedUid;
  lastCheckedName.value = requestedName;

  try {
    const response = await registrationClient.lookupUid({ uid: normalizedUid, name: requestedName });
    if (isStaleAnswer(normalizedUid, requestedName)) return;

    if (!response.verified) {
      uidCheckStatus.value = 'outage';
      uidCheckMessage.value = response.message ?? translator.get('registration.company.uid_check_unavailable');
      uidIsValid.value = true;
      return;
    }

    uidCheckCompanyName.value = response.companyName ?? '';
    uidCheckStatus.value = 'valid';
    uidIsValid.value = true;
    applyAutofill(response);
    // an autofilled name is not a mismatch, so it must not count as changed
    lastCheckedName.value = draft.value.name;
  } catch (error) {
    if (isStaleAnswer(normalizedUid, requestedName)) return;

    const message = readRegistrationErrorMessage(error);
    if (message) {
      uidCheckStatus.value = 'error';
      uidCheckMessage.value = message;
      uidIsValid.value = false;
      return;
    }

    // request failure says nothing about the UID: treat like an outage
    uidCheckStatus.value = 'outage';
    uidCheckMessage.value = translator.get('registration.company.uid_check_unavailable');
    uidIsValid.value = true;
  }
}

/** Runs the check on Enter/blur, not per keystroke or twice for the same value. */
function triggerCheck() {
  const normalized = normalizeUid(uid.value);
  if (!isValidUid(normalized)) return;
  if (alreadyCheckedFor(normalized)) return;

  runCheck(normalized);
}

function onUidKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter') return;
  event.preventDefault();
  triggerCheck();
}

function onUidBlur() {
  triggerCheck();
}

/** Re-runs the check with the current UID and name; the parent calls it on name blur and after UID hydration. */
function recheckWithCurrentName() {
  const normalized = normalizeUid(uid.value);
  if (!isValidUid(normalized)) return;
  if (alreadyCheckedFor(normalized)) return;

  runCheck(normalized);
}

defineExpose({ recheckWithCurrentName });

/* --------------------------------- WATCHERS --------------------------------- */
watch(
  uid,
  (value, oldValue) => {
    const normalized = normalizeUid(value);
    if (normalized === lastCheckedUid.value) return; // unchanged since the last resolved answer

    uidCheckMessage.value = '';
    uidIsValid.value = false;
    uidCheckStatus.value = classifyLocalUid(normalized);

    // confirm a UID already present at mount (resumed session)
    if (oldValue === undefined && isValidUid(normalized)) {
      runCheck(normalized);
    }
  },
  { immediate: true }
);

watchEffect(() => {
  nameConfirmed.value = uidCheckStatus.value === 'valid' && draft.value.name === lastCheckedName.value;
});
</script>
